import Tooltip from '@components/Tooltip'
import { LP_STATUS } from '@utils/constants/learningPath'
import { find, findIndex } from 'lodash'
import React, { useEffect, useState } from 'react'
import { ProgressionNavsProp } from './interface'
import {
  Container,
  Dot,
  DotContainer,
  DotItem,
  EmptyNavPlaceholder,
  IconHolder,
  Line,
  RightHolder,
  StyledArrowLeft,
  StyledArrowRight,
  StyledLocked,
  StyledTick,
  StyledTimeClock,
  StyledUnlocked,
} from './style'

const EMPTY_UNITS = []

const ProgressNav: React.FC<ProgressionNavsProp> = ({
  pageSize = 1,
  Units = EMPTY_UNITS,
  active,
  activeHover,
  setActive,
  scrollerId = 'scroller',
}) => {
  const firstUnit = Units[0]
  const lastUnit = Units[Units.length - 1]
  const noNavs = !Units.length || pageSize > Units.length
  const [hasReachedEnd, setHasReachedEnd] = useState(false)
  const [hasReachedStart, setHasReachedStart] = useState(true)
  const [page, setPage] = useState(Units.slice(0, pageSize))
  const [currentTile, setCurrentTile] = useState(0)
  const [startEnd, setStartEnd] = useState([0, pageSize])
  const [entries, setEntries] = useState([] as IntersectionObserverEntry[])
  const [scrollVal, setScrollVal] = useState(0)
  const [startEndEntries, setStartEndEntries] = useState([] as IntersectionObserverEntry[])

  useEffect(() => {
    setPage(Units.slice(startEnd[0], startEnd[1]))
  }, [Units, startEnd])

  useEffect(() => {
    if (!find(page, (Unit) => Unit.Id === currentTile) && currentTile) {
      const currentIndex = findIndex(Units, (Unit) => Unit.Id === currentTile)
      if (currentIndex < startEnd[0]) {
        setStartEnd([currentIndex, startEnd[1] - (startEnd[0] - currentIndex)])
      } else if (currentIndex >= startEnd[1]) {
        setStartEnd([currentIndex - startEnd[1] + startEnd[0] + 1, currentIndex + 1])
      }
    }
  }, [currentTile])

  useEffect(() => {
    const intersectingEntries = entries.filter((e) => e.isIntersecting) || []
    const currentScroll = document.getElementById(scrollerId)?.scrollLeft || 0
    const intersectingEntry = intersectingEntries[currentScroll < scrollVal ? 0 : intersectingEntries.length - 1]

    setScrollVal(currentScroll)
    setCurrentTile(Number(intersectingEntry?.target?.id?.split('-')[1]))
  }, [entries])

  useEffect(() => {
    const currentScroll = document.getElementById(scrollerId)?.scrollLeft || 0
    const entry = startEndEntries[currentScroll < scrollVal ? 0 : startEndEntries.length - 1]
    if (!entry) {
      return
    }

    if (!firstUnit || !lastUnit) {
      setHasReachedStart(true)
      setHasReachedEnd(true)
      return
    }

    const isStart = entry?.target?.id === `tile-${firstUnit.Id}-${firstUnit.LearningPathId}`
    const isEnd = entry?.target?.id === `tile-${lastUnit.Id}-${firstUnit.LearningPathId}`

    setHasReachedStart((isStart && entry.isIntersecting) || scrollVal === 0)
    setHasReachedEnd(isEnd && entry.isIntersecting)
  }, [firstUnit, lastUnit, scrollVal, scrollerId, startEndEntries])

  const scrollListener = (e) => {
    setEntries(e)
  }

  const startEndListener = (e) => {
    setStartEndEntries(e)
  }

  useEffect(() => {
    if (!firstUnit || !lastUnit) {
      setHasReachedEnd(true)
      return
    }

    try {
      const scroller = document.getElementById(scrollerId)
      const options = {
        root: scroller,
        rootMargin: '200px',
        threshold: 1,
      }

      const observer = new IntersectionObserver(scrollListener, options)
      const startEndObserver = new IntersectionObserver(startEndListener, options)

      const obsTile = Units.map((Unit) => document.getElementById(`tile-${Unit.Id}-${Unit.LearningPathId}`))
      const obsStartEnd = [
        document.getElementById(`tile-${firstUnit.Id}-${firstUnit.LearningPathId}`), // first tile
        document.getElementById(`tile-${lastUnit.Id}-${firstUnit.LearningPathId}`), // last tile
      ].filter(Boolean)

      obsTile.filter(Boolean).map((el) => observer.observe(el as HTMLDivElement))
      obsStartEnd.map((el) => startEndObserver.observe(el as HTMLDivElement))

      // this resets the scroller when changing module
      if (scroller) {
        scroller.scrollLeft = 0
        setActive(0)
      }

      return () => {
        observer.disconnect()
        startEndObserver.disconnect()
      }
    } catch (e) {
      // fail silently, incase the browser does not support IO
      setHasReachedEnd(true)
    }
  }, [Units, firstUnit, lastUnit, scrollerId, setActive])

  const clickCb = (toTheRight?: boolean) => {
    const tileSize = 400 // approximate card size
    const halfPageSize = Math.round(pageSize / 2)
    const scroller = document.getElementById(scrollerId)

    if (scroller) {
      scroller.scrollLeft = toTheRight
        ? scroller.scrollLeft + tileSize * halfPageSize
        : scroller.scrollLeft - tileSize * halfPageSize
    }
  }

  return (
    <Container className={`${noNavs && 'no-navs'}`}>
      {noNavs ? (
        <EmptyNavPlaceholder />
      ) : (
        <div
          onClick={() => {
            if (!hasReachedStart) {
              clickCb(false)
            }
          }}
        >
          <StyledArrowLeft color="brand03" disabled={hasReachedStart} />
        </div>
      )}
      <DotContainer className={`${noNavs && 'max-content'}`}>
        <Line className="pending" />

        {page?.map(({ Id, Status, Title }, index: number) => {
          const isActive = Id === active
          const isActiveHover = Id === activeHover

          const DotIcon = {
            [LP_STATUS.NOT_YET_STARTED]: <Dot active={isActive} />,
            [LP_STATUS.IN_PROGRESS]: <StyledTimeClock active={isActive} />,
            [LP_STATUS.COMPLETE]: <StyledTick active={isActive} />,
            [LP_STATUS.LOCKED]: <StyledLocked active={isActive} />,
            [LP_STATUS.UNLOCKED]: <StyledUnlocked active={isActive} />,
            [LP_STATUS.COURSE_META]: <Dot active={isActive} status={Status} />,
          }

          return (
            <React.Fragment key={`dot-item-${index}`}>
              <DotItem>
                <Tooltip message={Title} visible={isActiveHover}>
                  <IconHolder status={Status} active={isActive} onClick={() => setActive(Id)}>
                    {DotIcon[Status ?? ''] || DotIcon[LP_STATUS.NOT_YET_STARTED]}
                  </IconHolder>
                </Tooltip>
              </DotItem>
            </React.Fragment>
          )
        })}
      </DotContainer>
      {!noNavs && (
        <RightHolder
          onClick={() => {
            if (!hasReachedEnd) {
              clickCb(true)
            }
          }}
        >
          <StyledArrowRight color="brand03" disabled={hasReachedEnd} />
        </RightHolder>
      )}
    </Container>
  )
}

export default React.memo(ProgressNav)
